
import { supabase } from '@/integrations/supabase/client';
import { DealerRatingFormValues } from '@/types/dealerRating';

export type CreateDealerRatingData = Omit<DealerRatingFormValues, 'user_id'> & { user_id: string };

/**
 * Checks if a user has already rated a specific dealer.
 * @param dealerId - The ID of the dealer.
 * @param userId - The ID of the user.
 * @returns True if a rating exists, false otherwise.
 */
export const checkExistingRating = async (dealerId: string, userId: string): Promise<boolean> => {
  console.log('[checkExistingRating] Checking for existing rating:', { dealerId, userId });
  
  const { error, count } = await supabase
    .from('dealer_ratings')
    .select('*', { count: 'exact', head: true })
    .eq('dealer_id', dealerId)
    .eq('user_id', userId);

  if (error) {
    console.error('[checkExistingRating] Error checking existing rating:', error);
    throw new Error("Impossibile verificare la recensione esistente.");
  }

  console.log('[checkExistingRating] Result:', { count, exists: count !== null && count > 0 });
  return count !== null && count > 0;
};

/**
 * Creates a new dealer rating using an RPC function.
 * @param ratingData - The data for the new rating. The user_id is handled by the RPC.
 * @returns The created rating data.
 */
export const createDealerRating = async (ratingData: DealerRatingFormValues) => {
  console.log('[DEBUG] createDealerRating called with:', {
    dealer_id: ratingData.dealer_id,
    rating: ratingData.rating,
    comment: ratingData.comment ? 'present' : 'empty'
  });

  // Prima verifichiamo che l'utente sia autenticato
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError) {
    console.error('[DEBUG] Error getting session:', sessionError);
    throw new Error("Errore di autenticazione. Riprova ad accedere.");
  }
  
  if (!session || !session.user) {
    console.error('[DEBUG] No active session found');
    throw new Error("Devi essere autenticato per lasciare una recensione. Effettua il login.");
  }
  
  console.log('[DEBUG] User authenticated:', {
    userId: session.user.id,
    email: session.user.email,
    hasAccessToken: !!session.access_token
  });
  
  // Assicuriamoci che il client Supabase abbia il token di autenticazione aggiornato
  // Questo è importante per le chiamate RPC che dipendono da auth.uid()
  if (session.access_token) {
    console.log('[DEBUG] Updating session token');
    // Forziamo l'aggiornamento del token nel client
    await supabase.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token
    });
  }
  
  console.log('[DEBUG] Calling RPC create_user_dealer_rating with params:', {
    p_dealer_id: ratingData.dealer_id,
    p_rating: ratingData.rating,
    p_comment: ratingData.comment
  });

  // Usiamo il client Supabase principale che mantiene l'autenticazione dell'utente
  // Aggiungiamo un cast a `any` per bypassare il controllo dei tipi di TypeScript,
  // dato che la funzione RPC `create_user_dealer_rating` esiste nel DB ma non nei tipi generati.
  const { data, error } = await (supabase as any)
    .rpc('create_user_dealer_rating', {
      p_dealer_id: ratingData.dealer_id,
      p_rating: ratingData.rating,
      p_comment: ratingData.comment,
    });

  if (error) {
    console.error('[DEBUG] RPC Error details:', {
      error,
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code
    });
    // The RPC function raises an exception with a specific message.
    // We can pass this message directly to the user.
    throw new Error(error.message || "Impossibile salvare la recensione.");
  }

  console.log('[DEBUG] RPC call successful, data:', data);
  return data;
};
